import { useEffect, useMemo, useState } from 'react';

import path from 'path';

import { Link as MuiLink } from '@mui/material';
import { GridColDef, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { Link as ReactRouterLink } from 'react-router-dom';
import useSWR from 'swr';

import { DataGrid } from '~components';
import { getISOYearMonthString } from '~utils/dateUtils';

import TransactionSummary from '../../interfaces/transactionSummary';
import TransactionsService from '../../services/transactionsService';

const service = new TransactionsService();

/**
 *
 * @returns
 * Notice if you change the props to a spread syntax, will create infinite rerender
 */
const MonthlyTransactionsSummaryDataGrid = () => {
	const { i18n, t } = useTranslation();

	const [fetchParameters, setFetchParameters] = useState({});
	const { data, error, isLoading, isValidating } = useSWR(
		[path.join(service.basePath, 'list'), fetchParameters],
		([_, args]) => {
			if (isLoading) {
				service.abortCurrentRequest('Parameters change');
			}

			return service.getTransactionsSummary(args);
		}
	);
	const [totalCount, setTotalCount] = useState<number>(data?.total || 0);

	const columns = useMemo<GridColDef<TransactionSummary>[]>(
		() => [
			{
				field: 'start',
				headerName: t('ui.period'),
				renderCell: (params) => (
					<MuiLink
						component={ReactRouterLink}
						// to={params.value.toDateString()}
						to={getISOYearMonthString(params.value)}
					>
						{params.value.toLocaleString(i18n.language, { month: 'long', year: 'numeric' })}
					</MuiLink>
				),
				flex: 1,
				hideable: false,
			},
			// {
			// 	field: 'total',
			// 	headerName: t('ui.label.item'),
			// 	flex: 1,
			// },
			{
				field: 'count',
				headerName: t('transactions'),
				flex: 1,
			},
		],
		[t, i18n.language]
	);

	useEffect(() => {
		if (data?.total != null) {
			setTotalCount(data.total);
		}
	}, [data]);

	const handlePaginationModelChange = (value: GridPaginationModel) => {
		setFetchParameters((prev) => ({
			...prev,
			page: value.page + 1,
			pageSize: value.pageSize,
		}));
	};

	const handleSortModelChange = (model: GridSortModel) => {
		setFetchParameters((prev) => ({
			...prev,
			sortBy: model ? model[0]?.field : undefined,
			descending: model ? model[0]?.sort === 'desc' : undefined,
		}));
	};

	return (
		<DataGrid
			getRowId={(row) => row.start}
			error={error}
			loading={isLoading || isValidating}
			columns={columns}
			rows={data?.results ?? []}
			rowCount={totalCount}
			onPaginationModelChange={handlePaginationModelChange}
			onSortModelChange={handleSortModelChange}
			disableColumnSorting
			initialState={{
				pagination: {
					rowCount: 10,
				},
			}}
			sx={{ flexGrow: 1, flexBasis: 200 }}
		/>
	);
};

export default MonthlyTransactionsSummaryDataGrid;
